import React, { useState, useEffect } from 'react';
import { useUserStore } from '../store/userStore';
import { adminService } from '../services/adminService';
import { useNavigate, Link } from 'react-router-dom';

const AdminPage: React.FC = () => {
  const { isAdmin } = useUserStore();
  const navigate = useNavigate();
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [items, setItems] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedItem, setEditedItem] = useState<any>(null);

  // Redirect if not admin
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Fetch tables on mount
  useEffect(() => {
    const fetchTables = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const tablesList = await adminService.getTables();
        setTables(tablesList);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to fetch tables: ' + (err instanceof Error ? err.message : String(err)));
        setIsLoading(false);
      }
    };

    fetchTables();
  }, []);

  // Fetch items when a table is selected
  useEffect(() => {
    if (!selectedTable) return;

    const fetchItems = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const itemsList = await adminService.getItems(selectedTable);
        setItems(itemsList);
        setSelectedItem(null);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to fetch items: ' + (err instanceof Error ? err.message : String(err)));
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [selectedTable]);

  // Handle table selection
  const handleTableSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTable(e.target.value);
  };

  // Handle item selection
  const handleItemSelect = (item: any) => {
    setSelectedItem(item);
    setEditedItem(JSON.parse(JSON.stringify(item))); // Deep copy
    setIsEditing(false);
  };

  // Handle edit button click
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!selectedTable || !editedItem || !editedItem.id) return;

    try {
      setIsLoading(true);
      setError(null);
      await adminService.updateItem(selectedTable, editedItem.id, editedItem);
      
      // Refresh items
      const itemsList = await adminService.getItems(selectedTable);
      setItems(itemsList);
      
      // Find and select the updated item
      const updatedItem = itemsList.find(item => item.id === editedItem.id);
      setSelectedItem(updatedItem);
      
      setIsEditing(false);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to save item: ' + (err instanceof Error ? err.message : String(err)));
      setIsLoading(false);
    }
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    setEditedItem(JSON.parse(JSON.stringify(selectedItem))); // Reset to original
    setIsEditing(false);
  };

  // Handle field change in the editor
  const handleFieldChange = (key: string, value: any) => {
    setEditedItem((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Removed unused function: handleImageListChange

  // Add a new image to the list
  const handleAddImage = () => {
    if (!editedItem.detail_images) {
      setEditedItem((prev: any) => ({
        ...prev,
        detail_images: [{ id: { S: '' }, image: { S: '' } }],
      }));
    } else {
      setEditedItem((prev: any) => ({
        ...prev,
        detail_images: [
          ...prev.detail_images,
          { id: { S: '' }, image: { S: '' } },
        ],
      }));
    }
  };

  // Remove an image from the list
  const handleRemoveImage = (index: number) => {
    setEditedItem((prev: any) => ({
      ...prev,
      detail_images: prev.detail_images.filter((_: any, i: number) => i !== index),
    }));
  };

  // Update an image in the list
  const handleUpdateImage = (index: number, field: string, value: string) => {
    setEditedItem((prev: any) => {
      const newImages = [...prev.detail_images];
      newImages[index] = {
        ...newImages[index],
        [field]: { S: value },
      };
      return {
        ...prev,
        detail_images: newImages,
      };
    });
  };

  // Render the editor for a specific field
  const renderFieldEditor = (key: string, value: any) => {
    // Skip rendering the id field as it's not editable
    if (key === 'id') return null;

    // Handle different types of fields
    if (typeof value === 'object' && value !== null) {
      // Handle DynamoDB string type
      if (value.S !== undefined) {
        if (key === 'client_bio' || key.includes('features_') || key.includes('text') || key.includes('description')) {
          // Render textarea for long text fields
          return (
            <div key={key} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {key}
              </label>
              <textarea
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner resize-y focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                value={editedItem[key].S}
                onChange={(e) =>
                  handleFieldChange(key, { S: e.target.value })
                }
                rows={10}
                style={{ minHeight: '150px' }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{key}</span>
                <span>{editedItem[key].S.length} characters</span>
              </div>
            </div>
          );
        } else {
          // All string fields should be textarea for better editing of large text
          return (
            <div key={key} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {key}
              </label>
              <textarea
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner resize-y focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                value={editedItem[key].S}
                onChange={(e) =>
                  handleFieldChange(key, { S: e.target.value })
                }
                rows={4}
                style={{ minHeight: '80px' }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{key}</span>
                <span>{editedItem[key].S.length} characters</span>
              </div>
            </div>
          );
        }
      }
      // Handle DynamoDB list type (for detail_images)
      else if (key === 'detail_images' && value.L !== undefined) {
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {key}
            </label>
            <div className="border border-gray-300 rounded-md p-4">
              {editedItem[key].L.map((image: any, index: number) => (
                <div key={index} className="mb-4 border border-gray-200 p-3 rounded-md">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID
                    </label>
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-md resize-y"
                      placeholder="ID"
                      value={image.M.id.S}
                      onChange={(e) =>
                        handleUpdateImage(index, 'id', e.target.value)
                      }
                      rows={2}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Image Path
                    </label>
                    <textarea
                      className="w-full p-2 border border-gray-300 rounded-md resize-y"
                      placeholder="Image path"
                      value={image.M.image.S}
                      onChange={(e) =>
                        handleUpdateImage(index, 'image', e.target.value)
                      }
                      rows={2}
                    />
                  </div>
                  <div className="text-right">
                    <button
                      className="bg-red-500 text-white px-3 py-1 rounded-md"
                      onClick={() => handleRemoveImage(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-md mt-2"
                onClick={handleAddImage}
              >
                Add Image
              </button>
            </div>
          </div>
        );
      }
      // For other complex types, render as JSON
      else {
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {key} (Complex Type)
            </label>
            <div className="relative">
              <textarea
                className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner resize-y focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all font-mono"
                value={JSON.stringify(editedItem[key], null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    handleFieldChange(key, parsed);
                  } catch (err) {
                    // Ignore parse errors while typing
                  }
                }}
                rows={10}
                style={{ minHeight: '150px' }}
              />
              <div className="absolute top-2 right-2">
                <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md shadow-sm">
                  JSON
                </div>
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>{key}</span>
              <span>Edit as JSON - Format must be valid JSON</span>
            </div>
          </div>
        );
      }
    }
    // For primitive types
    else {
      return (
        <div key={key} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {key}
          </label>
          <textarea
            className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 shadow-inner resize-y focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
            value={editedItem[key]}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            rows={4}
            style={{ minHeight: '80px' }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{key}</span>
            <span>{String(editedItem[key]).length} characters</span>
          </div>
        </div>
      );
    }
  };

  return (
    <div 
      className="min-h-screen p-4"
      style={{
        backgroundImage: 'url("/images/admin/admin-background-elegant.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Navigation Bar */}
      <nav className="max-w-6xl mx-auto mb-6">
        <div className="flex justify-center gap-2">
          <Link
            to="/"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            🏠 Home
          </Link>
          <Link
            to="/admin/create-project"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            🎨 Create Project
          </Link>
          <Link
            to="/file-sharing"
            className="bg-white/80 hover:bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            📁 File Sharing
          </Link>
          <Link
            to="/admin"
            className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-medium text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 ring-2 ring-blue-500"
          >
            ⚙️ Admin
          </Link>
        </div>
      </nav>

      <header className="mb-8 text-center animate-[fadeIn_0.5s_ease-out]">
        <div className="flex justify-center mb-4">
          <img src="/images/admin/ed-character-logo.png" alt="Ed Character Logo" className="h-32" />
        </div>
        <h1 className="text-4xl font-bold mb-2 text-primary drop-shadow-md">Evolution Design Inc Admin</h1>
        <p className="text-lg text-muted-foreground mb-4">
          Interior Design Content Management
        </p>
        <div className="bg-white/70 backdrop-blur-sm py-2 px-6 rounded-full inline-block">
          <p className="text-primary font-medium">
            Edit and update your website content
          </p>
        </div>
      </header>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="glass-effect rounded-xl shadow-xl p-6 mb-6 border border-white/20 backdrop-blur-md animate-[fadeIn_0.6s_ease-out]">
        <div className="mb-4">
          <label className="block text-lg font-medium text-primary mb-2">
            Select Table
          </label>
          <div className="relative">
            <select
              className="w-full p-3 border border-gray-200 rounded-lg bg-white/90 text-lg shadow-inner focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
              value={selectedTable}
              onChange={handleTableSelect}
              disabled={isLoading}
            >
              <option value="">Select a table</option>
              {tables.map((table) => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      {isLoading && (
        <div className="text-center p-8 glass-effect rounded-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-white font-medium drop-shadow-md">Loading...</p>
        </div>
      )}
      
      {selectedTable && !isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-[fadeIn_0.7s_ease-out]">
          {/* Items List */}
          <div className="md:col-span-1 glass-effect rounded-xl shadow-xl p-6 h-[calc(100vh-300px)] overflow-y-auto border border-white/20 backdrop-blur-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-primary">Content Items</h2>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {items.length} items
              </span>
            </div>
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-gray-600 italic">No items found</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedItem && selectedItem.id === item.id
                        ? 'bg-blue-100/80 shadow-md border-l-4 border-blue-500'
                        : 'hover:bg-white/80 hover:shadow-md border-l-4 border-transparent'
                    }`}
                    onClick={() => handleItemSelect(item)}
                  >
                    <div className="font-medium">
                      {item.id} {item.name && `- ${item.name.S || item.name}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Item Details */}
          <div className="md:col-span-2 glass-effect rounded-xl shadow-xl p-6 h-[calc(100vh-300px)] overflow-y-auto border border-white/20 backdrop-blur-md">
            {selectedItem ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">
                    {isEditing ? 'Edit Item' : 'Item Details'}
                  </h2>
                  <div>
                    {isEditing ? (
                      <>
                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-md mr-3 transition-colors shadow-md"
                          onClick={handleSaveClick}
                          disabled={isLoading}
                        >
                          Save
                        </button>
                        <button
                          className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-2 rounded-md transition-colors shadow-md"
                          onClick={handleCancelClick}
                          disabled={isLoading}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-md transition-colors shadow-md"
                        onClick={handleEditClick}
                        disabled={isLoading}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                
                {isEditing ? (
                  // Edit form
                  <div className="bg-white/90 p-6 rounded-lg shadow-inner border border-gray-100">
                    <div className="mb-4 pb-3 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-blue-600 uppercase tracking-wider">Editing Item: {editedItem.id}</h3>
                    </div>
                    {Object.entries(editedItem).map(([key, value]) =>
                      renderFieldEditor(key, value)
                    )}
                  </div>
                ) : (
                  // View details
                  <div className="space-y-4 bg-white/80 p-6 rounded-lg shadow-inner">
                    {Object.entries(selectedItem).map(([key, value]) => (
                      <div key={key} className="border-b border-gray-200 pb-4 last:border-0">
                        <h3 className="font-semibold text-gray-700 mb-2">{key}</h3>
                        <pre className="bg-gray-50 p-3 rounded-md overflow-x-auto text-sm">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
                <p className="text-xl">Select an item to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
